import { useCallback, useEffect, useState } from 'react';
import { MediaField, PropertyField as Field } from '../../../../system/field';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AUTOSAVE_INTERVAL,
  KEYSTONE_PREVIEW_URL,
  KEYSTONE_REDIRECT_URL,
} from '../../../../config';
import {
  formatSystemError,
  navigateTo,
  toFormattedPrice,
  toFormattedTime,
  toHTTPSURL,
  toStandardDateFormat,
} from '../../../../utils';
import { calculateRoomSummary, Room } from '../../control/LevelsAndRooms';
import { formatAddressLineOne } from '../../../../system/location';
import { useModal } from '../../../../contexts/ModalContext';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../flags';
import { useAuth } from '../../../../contexts/AuthContext';
import { Permissions } from '../../../../system/permission';
import { MediaCategory } from '../../../../system/media';
import {
  ListingResource,
  useFormProgressStore,
  useListingActions,
  useListingField,
  useListingMetadata,
  useListingResource,
  useListingStatus,
  useMediaResource,
} from '@brightmls/nextgen.lae.flc';
import { useEditListing } from '../../../../contexts/EditListingContext';
import { isResidentalListing } from '../../../../system/property-type';
import { useShareDraft } from '../../hooks/useShareDraft/useShareDraft';
import { useService } from '../../../../contexts/ServiceContext';

export const useListingHeaderLogic = () => {
  const navigate = useNavigate();
  const { listingKey } = useParams();
  const location = useLocation();
  const { allowed } = useAuth();
  const { showModal } = useModal();
  const { hasFeature, setTrait } = useFeatureFlags();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [autosaved, setAutosaved] = useState<null | string>(null);
  const [autosaveStatus, setAutoSaveStatus] = useState<null | string>(null);
  const [autosaveError, setAutosaveError] = useState<string | null>(null);
  const [saveDraftStatus, setSaveDraftStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [saveDraftError, setSaveDraftError] = useState<string | null>(null);
  const [isReturnToLegacy, setIsReturnToLegacy] = useState<boolean>(false);
  const [isAtBaseRoute, setIsAtBaseRoute] = useState(true);
  const { publish, exit } = useEditListing();
  const { saveListing } = useListingActions();
  const { onShareDraft } = useShareDraft();
  const { key, isFromLiveListing, published, discarded } = useListingMetadata();
  const { isRequestInProgress, getCurrentRequestPromise } = useService();

  const hasErrors = useFormProgressStore(
    (state) => state.actualTotalErrorCount > 0,
  );
  const meta = useListingMetadata();

  const streetDirPrefix = useListingField(
    ListingResource.Property,
    Field.STREET_DIR_PREFIX,
    '',
  );
  const streetSuffix = useListingField(
    ListingResource.Property,
    Field.STREET_SUFFIX,
    '',
  );
  const streetNumber = useListingField(
    ListingResource.Property,
    Field.STREET_NUMBER,
    '',
  );
  const streetModifier = useListingField(
    ListingResource.Property,
    Field.STREET_SUFFIX_MODIFIER,
    '',
  );
  const streetDirSuffix = useListingField(
    ListingResource.Property,
    Field.STREET_DIR_SUFFIX,
    '',
  );
  const streetName = useListingField(
    ListingResource.Property,
    Field.STREET_NAME,
    '',
  );
  const unitNumber = useListingField(
    ListingResource.Property,
    Field.UNIT_NUMBER,
    '',
  );
  const latitude = useListingField(ListingResource.Property, Field.LATITUDE, 0);
  const longitude = useListingField(
    ListingResource.Property,
    Field.LONGITUDE,
    0,
  );
  const propertyType = useListingField(
    ListingResource.Property,
    Field.PROPERTY_TYPE,
    '',
  );

  // RES, RESI, RESL -- match any property types targeting residental listings
  const isResidental = isResidentalListing(propertyType);

  // determine if the user is in the enrollment program, if so they can disable NGW
  const isEnrollmentUser = hasFeature(FeatureFlags.NGW_ENROLLMENT);

  const isNavigatingAway =
    useListingStatus<boolean>('isNavigatingAway', false).value === true;

  useEffect(() => {
    const isMediaRoute = /\/(photos|tours|documents)$/.test(location.pathname);
    if (isMediaRoute) {
      setIsAtBaseRoute(false);
    }
  }, [location.pathname]);

  // Format address into two lines
  const addressLine1 = formatAddressLineOne({
    streetModifier: streetModifier.value,
    streetDirPrefix: streetDirPrefix.value,
    streetDirSuffix: streetDirSuffix.value,
    streetName: streetName.value,
    streetNumber: streetNumber.value,
    streetSuffix: streetSuffix.value,
    unitNumber: unitNumber.value,
  });

  const address = addressLine1
    ? [addressLine1].filter(Boolean).join('\n')
    : 'Not Set';

  const status = useListingField(
    ListingResource.Property,
    Field.MLS_STATUS,
    'Draft',
  );
  const price = useListingField(
    ListingResource.Property,
    Field.LIST_PRICE,
    'Not Set',
  );
  const expirationDate = useListingField(
    ListingResource.Property,
    Field.EXPIRATION_DATE,
    'Not Set',
  );
  const officeExclusiveYN = useListingField(
    ListingResource.Property,
    Field.OFFICE_EXCLUSIVE_YN,
    false,
  );

  const formattedExpirationDate =
    expirationDate.value === 'Not Set'
      ? expirationDate.value
      : toStandardDateFormat(expirationDate.value, 'MMM DD, YYYY');
  const formattedPrice =
    price.value === 'Not Set'
      ? 'No List Price'
      : toFormattedPrice(price.value, 'No List Price');

  // Show display status
  const isOfficeExclusive = Boolean(officeExclusiveYN.value);

  // Apply masking rules based on Office Exclusive setting
  let formattedStatus = status.value.replace('-BRIGHT', '');

  if (isOfficeExclusive) {
    if (formattedStatus === 'ACTIVE') {
      formattedStatus = 'Office Exclusive';
    } else if (formattedStatus !== 'Not Set') {
      formattedStatus = `${formattedStatus} (Office Exclusive)`;
    }
  } else if (formattedStatus === 'OFFICE EXCLUSIVE') {
    formattedStatus = 'ACTIVE';
  }

  const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setAnchorEl(null);
  };

  const handlePublish = () => {
    publish();
  };

  const handleSaveDraft = async () => {
    setSaveDraftStatus('loading');
    setSaveDraftError(null);
    handleActionsClose();

    try {
      await saveListing();

      setAutosaved(toFormattedTime());
      setAutoSaveStatus('success');
      setSaveDraftStatus('success');

      console.log('Draft saved successfully');
    } catch (error: any) {
      setSaveDraftStatus('error');
      setSaveDraftError(formatSystemError(error));
    }

    // Reset status after 5 seconds
    setTimeout(() => {
      setSaveDraftStatus('idle');
      setSaveDraftError(null);
    }, 5000);
  };

  const handlePreviewDraft = async () => {
    if (hasErrors) {
      showModal({
        content:
          'This listing has missing required fields and is not available to be previewed. Please return to the form and provide information to the missing required fields and try again.',
        cancelText: 'Edit Listing', // optional, defaults to "Confirm"
      });
      console.warn('Can not preview with errors on the form');
      return;
    }

    // Validation: Check if listing is already published (and not office exclusive)
    if (isFromLiveListing && !isOfficeExclusive) {
      showModal({
        content:
          'This listing has already been published and is not available to be previewed.',
        cancelText: 'OK',
      });
      return;
    }

    await handleSaveDraft();

    navigateTo(`${KEYSTONE_PREVIEW_URL}${key}`, true);
    handleActionsClose();
  };

  const isAbleToPublish = () => {
    if (isFromLiveListing) {
      return allowed({
        permission: Permissions.PUBLISH_CHANGES_TO_EXISTING_LISTINGS,
      });
    }
    return allowed({
      permission: Permissions.PUBLISH_NEW_LISTINGS,
    });
  };

  const handleReturnToLegacy = () => {
    setIsReturnToLegacy(true);
    showModal({
      title: 'Switch back to legacy experience',
      width: 'xs',
      content:
        'Missing the classic look? You can return to the legacy version anytime—your data will stay the same.',
      confirmText: 'Stay With New Experience',
      cancelText: 'Back To Legacy',
      switchLayout: false,
      onConfirm: () => {
        setIsReturnToLegacy(false); // Stay with new experience
      },
      onCancel: async () => {
        try {
          await saveListing();
        } catch (e) {
          console.error('Could not save listing');
        }

        await setTrait(FeatureFlags.NGW_ENROLLMENT, false);

        navigateTo(`${KEYSTONE_REDIRECT_URL}#Edit:${key}`); // Go back to legacy
      },
      onDismiss: () => {
        setIsReturnToLegacy(false); // Reset switch if modal is dismissed
      },
    });
  };

  const handleNavigateBack = useCallback(() => {
    const currentPath = location.pathname;
    const baseEditPath = `/edit/${listingKey}`;
    const isMediaRoute = currentPath.includes('/media');

    if (isMediaRoute && !isAtBaseRoute) {
      // First click when in media section - navigate to base edit route
      navigate(baseEditPath, { state: { from: 'media' } });
      setIsAtBaseRoute(true);
    } else {
      // Second click on base route or direct click on base route
      exit(hasErrors, meta.isFromLiveListing);
    }
  }, [location.pathname, listingKey, navigate, exit, hasErrors, isAtBaseRoute]);

  const handleShareDraft = useCallback(() => {
    handleActionsClose();

    onShareDraft(!hasErrors);
  }, [hasErrors, onShareDraft]);

  const waitForRequestCompletion = useCallback(async (): Promise<void> => {
    if (isRequestInProgress()) {
      const requestPromise = getCurrentRequestPromise();
      if (requestPromise) {
        try {
          await requestPromise;
          console.log('Active operations completed, proceeding with autosave');
        } catch (requestError) {
          // Log the error but continue with autosave
          console.log(
            'Active operations failed, but continuing with autosave:',
            requestError,
          );
        }
      }
    }
  }, [isRequestInProgress, getCurrentRequestPromise]);

  const autoSave = useCallback(async () => {
    if (published) {
      console.warn('Listing already published, will not auto save');
      return;
    }

    if (discarded) {
      console.warn('Listing already discarded, will not auto save');
      return;
    }
    // Wait for any active operations (saves, uploads, etc.) to complete before autosaving
    await waitForRequestCompletion();

    setAutoSaveStatus('success');

    try {
      await saveListing();
      setAutoSaveStatus('success');
      setAutosaved(toFormattedTime());
      setAutosaveError(null);
    } catch (error: any) {
      setAutosaved(toFormattedTime());
      setAutoSaveStatus('failed');
      setAutosaveError(formatSystemError(error));
    }
  }, [saveListing, published, waitForRequestCompletion]);

  useEffect(() => {
    const interval = setInterval(autoSave, AUTOSAVE_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [autoSave]);

  // Get summary values
  const { items: rooms } = useListingResource<Room>(ListingResource.Room);
  const { media: photos } = useMediaResource(MediaCategory.Photo);

  const mainImage = photos.sort(
    (photoA: any, photoB: any) =>
      photoA[MediaField.MEDIA_DISPLAY_ORDER] -
      photoB[MediaField.MEDIA_DISPLAY_ORDER],
  )[0];

  const listPicture = mainImage
    ? toHTTPSURL(mainImage[MediaField.MEDIA_URL_MEDIUM])
    : null;

  // Calculate summary using existing logic
  const summary = calculateRoomSummary(rooms);

  // Calculate totals using the summary data
  const bedroomsCount =
    summary.mainLevel.bedrooms +
    Object.values(summary.upperLevels).reduce(
      (sum, level) => sum + level.bedrooms,
      0,
    ) +
    Object.values(summary.lowerLevels).reduce(
      (sum, level) => sum + level.bedrooms,
      0,
    );

  const bathroomsFullCount =
    summary.mainLevel.fullBaths +
    Object.values(summary.upperLevels).reduce(
      (sum, level) => sum + level.fullBaths,
      0,
    ) +
    Object.values(summary.lowerLevels).reduce(
      (sum, level) => sum + level.fullBaths,
      0,
    );

  const bathroomsHalfCount =
    summary.mainLevel.halfBaths +
    Object.values(summary.upperLevels).reduce(
      (sum, level) => sum + level.halfBaths,
      0,
    ) +
    Object.values(summary.lowerLevels).reduce(
      (sum, level) => sum + level.halfBaths,
      0,
    );

  const bathroomsTotalCount = bathroomsFullCount + bathroomsHalfCount;

  // Get levels count from story list
  const levelsCount = summary.storyList.length;

  return {
    navigate,
    hasErrors,
    anchorEl,
    autosaved,
    autosaveStatus,
    autosaveError,
    saveDraftStatus,
    saveDraftError,
    address,
    formattedStatus,
    formattedPrice,
    formattedExpirationDate,
    beds: bedroomsCount,
    baths: bathroomsTotalCount,
    stories: levelsCount,
    listPicture,
    isReturnToLegacy,
    isEnrollmentUser,
    isAbleToPublish,
    handleReturnToLegacy,
    handleActionsClick,
    handleActionsClose,
    handleSaveDraft,
    handlePreviewDraft,
    handleShareDraft,
    handlePublish,
    isResidental,
    handleNavigateBack,
    isNavigatingAway,
    latitude: latitude.value,
    longitude: longitude.value,
    propertyType: propertyType.value,
  };
};
